import { IDate } from '@models';
import { formatDateWithOffset, isTimeTbc, TBC_TIME } from '@utils';
import { Col, DatePicker, Row, Select } from 'antd';
import dayjs from 'dayjs';
import React from 'react';

interface IProps {
  dateTime: IDate | undefined;
  setDate: (value: IDate) => void;
  /** Lets the date be cleared; the caller decides what "no date" means (e.g. not scheduled). */
  onClear?: () => void;
  /** Shown but not editable - the value was decided elsewhere (e.g. by a calendar slot). */
  disabled?: boolean;
  /**
   * Offers "TBC" as a time - the day is known, the hour not yet (stored as midnight, see
   * isTimeTbc) - and makes it the time a freshly picked date gets. For booking slots; a drop-off
   * is always a real time.
   */
  allowTbc?: boolean;
}

export const DateTimeSelect: React.FC<IProps> = ({ dateTime, setDate, onClear, disabled, allowTbc }) => {
  const handleDateChange = async (date: dayjs.Dayjs | null) => {
    if (!date) {
      onClear?.();
      return;
    }
    const current = dateTime?.startAt ? dayjs(dateTime.startAt) : undefined;
    const startH = current ? current.hour() : allowTbc ? 0 : 8;
    const startM = current ? current.minute() : 0;
    const start = dayjs(date).set('hour', startH).set('minute', startM);
    setDate({ startAt: formatDateWithOffset(start) });
  };

  const handleTimeChange = async (value: string) => {
    const time = value.split(':');
    const start = dayjs(dateTime?.startAt).hour(Number(time[0])).minute(Number(time[1])).second(0);
    setDate({ startAt: formatDateWithOffset(start) });
  };

  const options = allowTbc ? [{ value: TBC_TIME, label: 'TBC' }, ...timeSelectOptions] : timeSelectOptions;

  // Empty until there is a date - an optional field must not look pre-filled. Midnight reads
  // as "TBC" where that is on offer; a time off the 15-minute grid falls back to 08:00.
  const timeValue = (() => {
    if (!dateTime?.startAt) return undefined;
    if (allowTbc && isTimeTbc(dateTime.startAt)) return TBC_TIME;
    const hhmm = dayjs(dateTime.startAt).format('HH:mm');
    return timeSelectOptions.some((time) => time.value === hhmm) ? hhmm : '08:00';
  })();

  return (
    <Row gutter={8} wrap={false} style={{ width: '100%' }}>
      <Col flex="1">
        <DatePicker
          inputReadOnly
          disabled={disabled}
          showTime={false}
          allowClear={!!onClear}
          placeholder="Select date"
          disabledDate={(current) => current < dayjs().startOf('day')}
          format="DD/MM/YYYY"
          style={{ width: '100%' }}
          value={dateTime?.startAt ? dayjs(dateTime?.startAt) : undefined}
          onChange={handleDateChange}
        />
      </Col>
      <Col flex="1">
        <Select
          disabled={disabled}
          placeholder="Start time"
          style={{ width: '100%', height: 32 }}
          options={options}
          value={timeValue}
          onChange={(e) => handleTimeChange(e)}
        />
      </Col>
    </Row>
  );
};

interface IOption {
  value: string;
  label: string;
}

const timeSelectOptions: IOption[] = Array.from({ length: 13 }, (_, hour) =>
  Array.from({ length: 4 }, (_, index) => {
    const hourValue = (hour + 8) % 24;
    const minuteValue = index * 15;
    const formattedHour = hourValue.toString().padStart(2, '0');
    const formattedMinute = minuteValue.toString().padStart(2, '0');
    const time = `${formattedHour}:${formattedMinute}`;
    return { value: time, label: time };
  })
).flat();
